// LEGAL REGISTER — client access to Cardmoji's own legal documents.
//
// These are CARDMOJI's documents, where Cardmoji is controller: tenant-neutral,
// no merge fields. Per-tenant white-label documents (where the BANK is
// controller and {{bankName}} is correct) live in Policy templates under
// Tenants and are NOT part of this register.
//
// Rules this module exists to enforce at the call site:
//  - Every acceptance must say where it came from. obtained_via is a required
//    argument, never defaulted, so a missing channel is a type error here.
//  - THERE IS NO CHANNEL MEANING "SIGNING IN", AND THERE NEVER WILL BE ONE.
//    Signing in is authentication; it is not consent to anything.
//  - The version SHOWN is the version RECORDED. Always pass back the version
//    that came out of loadCurrentLegalDocument; the server re-resolves the
//    current version in the same statement and refuses on a mismatch, so a
//    publish mid-read cannot mis-record.

import { supabase } from "@/integrations/supabase/client";

/**
 * The three places an acceptance can be obtained. This union MIRRORS the
 * legal_consent_channels table, which is the single authority — the database
 * rejects any value that is not a row there. Recorded values can never be
 * renamed afterwards, so these strings are permanent.
 */
export type LegalConsentChannel = "consent_step" | "account_settings" | "policy_page";

export const LEGAL_CONSENT_CHANNELS: readonly LegalConsentChannel[] = [
  "consent_step",
  "account_settings",
  "policy_page",
] as const;

/**
 * Document keys the register currently holds.
 *
 * `biometric` is PUBLISHED AND VERSIONED here but DISPLAY-ONLY: the affirmative
 * act is the separate biometric purpose consent, enforced server-side before
 * generation. legal_document_keys is the authority; the database refuses an
 * acceptance of a display-only key.
 */
export type LegalDocKey = "terms" | "privacy" | "biometric";

export const LEGAL_DOC_KEYS: readonly LegalDocKey[] = ["privacy", "terms", "biometric"] as const;

/**
 * The public page for a register document. Pass a version to address the exact
 * text somebody read — a consumer who accepted v1 must be able to read v1, not
 * whatever is current now.
 */
export function legalDocPath(docKey: string, version?: number): string {
  return version ? `/legal/${docKey}/v/${version}` : `/legal/${docKey}`;
}


export interface LegalDocument {
  documentId: string;
  docKey: string;
  version: number;
  title: string;
  bodyMarkdown: string;
  bodySha256: string;
  publishedAt: string | null;
}

export interface LegalGateRow {
  docKey: string;
  requiredVersion: number | null;
  acceptedVersion: number | null;
  satisfied: boolean;
}

/** The published version of a document, resolved server-side. Drafts are unreachable. */
export async function loadCurrentLegalDocument(docKey: string): Promise<LegalDocument | null> {
  const { data, error } = await supabase.rpc("current_legal_document", { p_doc_key: docKey });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    documentId: row.o_document_id,
    docKey: row.o_doc_key,
    version: row.o_version,
    title: row.o_title,
    bodyMarkdown: row.o_body_md,
    bodySha256: row.o_body_sha256,
    publishedAt: row.o_published_at,
  };
}

/**
 * Record an acceptance of the exact version that was rendered.
 *
 * @param renderedVersion the version returned by loadCurrentLegalDocument and
 *   actually shown to the person — not a constant, and not re-read here.
 */
export async function acceptLegalDocument(args: {
  docKey: string;
  renderedVersion: number;
  obtainedVia: LegalConsentChannel;
  meta?: Record<string, unknown>;
}): Promise<string> {
  const { data, error } = await supabase.rpc("accept_legal_document", {
    p_doc_key: args.docKey,
    p_rendered_version: args.renderedVersion,
    p_obtained_via: args.obtainedVia,
    p_meta: (args.meta ?? {}) as never,
  });
  if (error) throw error;
  return data as unknown as string;
}

/** A withdrawal is a NEW record, never an edit of the acceptance. */
export async function withdrawLegalConsent(args: {
  docKey: string;
  obtainedVia: LegalConsentChannel;
  meta?: Record<string, unknown>;
}): Promise<string> {
  const { data, error } = await supabase.rpc("withdraw_legal_consent", {
    p_doc_key: args.docKey,
    p_obtained_via: args.obtainedVia,
    p_meta: (args.meta ?? {}) as never,
  });
  if (error) throw error;
  return data as unknown as string;
}

/**
 * Whether the signed-in person has accepted the current version of each
 * required document. The required list lives in the database configuration
 * function, not here, so a gate can never drift from it.
 */
export async function loadLegalGateStatus(docKeys?: string[]): Promise<LegalGateRow[]> {
  const { data, error } = docKeys
    ? await supabase.rpc("legal_gate_status", { p_doc_keys: docKeys })
    : await supabase.rpc("legal_gate_status");
  if (error) throw error;
  return ((data ?? []) as never[]).map((row: never) => {
    const r = row as unknown as {
      o_doc_key: string;
      o_required_version: number | null;
      o_accepted_version: number | null;
      o_satisfied: boolean;
    };
    return {
      docKey: r.o_doc_key,
      requiredVersion: r.o_required_version,
      acceptedVersion: r.o_accepted_version,
      satisfied: r.o_satisfied,
    };
  });
}

/** The document as it stood when it was accepted. Historical rendering. */
export async function loadDocumentAsAccepted(acceptanceId: string) {
  const { data, error } = await supabase.rpc("legal_document_as_accepted", {
    p_acceptance_id: acceptanceId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    docKey: row.o_doc_key,
    version: row.o_version,
    title: row.o_title,
    bodyMarkdown: row.o_body_md,
    recordedAt: row.o_recorded_at,
    obtainedVia: row.o_obtained_via as LegalConsentChannel,
    bodyMatches: row.o_body_matches as boolean,
  };
}

// ---------------------------------------------------------------------------
// HISTORICAL RENDERING — the property the register exists for.
//
// Both readers are SECURITY INVOKER server-side, so the legal_documents policy
// (non-draft only) applies: a DRAFT IS UNREACHABLE from the public pages.
// ---------------------------------------------------------------------------

export interface LegalDocumentVersionRow {
  version: number;
  title: string;
  status: "published" | "retired";
  bodySha256: string;
  publishedAt: string | null;
  retiredAt: string | null;
}

/** Every published-or-retired version of a document, newest first. */
export async function loadLegalDocumentVersions(docKey: string): Promise<LegalDocumentVersionRow[]> {
  const { data, error } = await supabase.rpc("legal_document_versions", { p_doc_key: docKey });
  if (error) throw error;
  return ((data ?? []) as never[]).map((row: never) => {
    const r = row as unknown as {
      o_version: number;
      o_title: string;
      o_status: string;
      o_body_sha256: string;
      o_published_at: string | null;
      o_retired_at: string | null;
    };
    return {
      version: r.o_version,
      title: r.o_title,
      status: r.o_status as "published" | "retired",
      bodySha256: r.o_body_sha256,
      publishedAt: r.o_published_at,
      retiredAt: r.o_retired_at,
    };
  });
}

export interface LegalDocumentAtVersion extends LegalDocument {
  status: "published" | "retired";
  retiredAt: string | null;
}

/** One exact version, by number. Returns null for a draft or an unknown version. */
export async function loadLegalDocumentAtVersion(
  docKey: string,
  version: number,
): Promise<LegalDocumentAtVersion | null> {
  const { data, error } = await supabase.rpc("legal_document_at_version", {
    p_doc_key: docKey,
    p_version: version,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    documentId: row.o_document_id,
    docKey: row.o_doc_key,
    version: row.o_version,
    title: row.o_title,
    bodyMarkdown: row.o_body_md,
    bodySha256: row.o_body_sha256,
    publishedAt: row.o_published_at,
    status: row.o_status as "published" | "retired",
    retiredAt: row.o_retired_at,
  };
}
